import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPostBySlug, getRelatedPosts } from '../data/blogData';
import BlogCard from '../components/BlogCard';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : null;

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📝</div>
          <h1 className="text-3xl font-bold text-white mb-4">Article Not Found</h1>
          <p className="text-gray-400 mb-8">The article you're looking for doesn't exist or has been moved.</p>
          <Link to="/blog" className="btn-primary">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = getRelatedPosts(post);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'ai': 'blue',
      'data-engineering': 'purple',
      'career': 'green',
      'tutorials': 'orange'
    };
    return colors[category] || 'gray';
  };

  // Simple markdown-to-JSX renderer for basic content
  const renderContent = (content: string) => {
    // Split content by double newlines to get paragraphs
    const sections = content.trim().split('\n\n');
    
    return sections.map((section, index) => {
      // Handle headers
      if (section.startsWith('# ')) {
        return (
          <h1 key={index} className="text-4xl font-bold text-white mb-6 mt-8">
            {section.substring(2)}
          </h1>
        );
      }
      if (section.startsWith('## ')) {
        return (
          <h2 key={index} className="text-2xl font-bold text-white mb-4 mt-6">
            {section.substring(3)}
          </h2>
        );
      }
      if (section.startsWith('### ')) {
        return (
          <h3 key={index} className="text-xl font-semibold text-white mb-3 mt-4">
            {section.substring(4)}
          </h3>
        );
      }
      
      // Handle code blocks
      if (section.includes('```')) {
        const codeMatch = section.match(/```(\w+)?\n([\s\S]*?)```/);
        if (codeMatch) {
          return (
            <div key={index} className="my-6">
              <pre className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 overflow-x-auto">
                <code className="text-green-400 text-sm font-mono whitespace-pre">
                  {codeMatch[2]}
                </code>
              </pre>
            </div>
          );
        }
      }
      
      // Handle lists
      if (section.includes('- ')) {
        const listItems = section.split('\n').filter(line => line.trim().startsWith('- '));
        return (
          <ul key={index} className="text-gray-300 mb-4 space-y-2 pl-6">
            {listItems.map((item, itemIndex) => (
              <li key={itemIndex} className="list-disc">
                {item.substring(2)}
              </li>
            ))}
          </ul>
        );
      }
      
      // Handle regular paragraphs
      if (section.trim() && !section.startsWith('*') && !section.startsWith('---')) {
        return (
          <p key={index} className="text-gray-300 mb-4 leading-relaxed">
            {section.trim()}
          </p>
        );
      }
      
      // Handle horizontal rules
      if (section.trim() === '---') {
        return <hr key={index} className="border-gray-700 my-8" />;
      }
      
      return null;
    }).filter(Boolean);
  };

  return (
    <div className="min-h-screen py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <div className="flex items-center space-x-2 text-gray-400">
            <Link to="/" className="hover:text-blue-400 transition-colors">Home</Link>
            <span>•</span>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link>
            <span>•</span>
            <span className="text-gray-500">{post.title}</span>
          </div>
        </nav>

        {/* Article Header */}
        <header className="mb-12">
          {/* Category Badge */}
          <div className="mb-6">
            <span className={`px-3 py-1 bg-${getCategoryColor(post.category)}-500/20 text-${getCategoryColor(post.category)}-300 text-sm font-medium rounded-full border border-${getCategoryColor(post.category)}-500/30`}>
              {post.category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-6">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {post.author.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">{post.author.name}</p>
              </div>
            </div>
            <span>•</span>
            <span>{formatDate(post.publishedDate)}</span>
            <span>•</span>
            <span>{post.readingTime} min read</span>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-gray-800/50 text-gray-300 text-sm rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </header>

        {/* Featured Image Placeholder */}
        <div className="mb-12">
          <div className="aspect-video bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">
                  {post.category === 'ai' ? '🤖' : 
                   post.category === 'data-engineering' ? '⚙️' : 
                   post.category === 'career' ? '🚀' : '📚'}
                </span>
              </div>
              <p className="text-gray-400">Featured Article Image</p>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="prose prose-lg prose-invert max-w-none">
          {renderContent(post.content)}
        </article>

        {/* Article Footer */}
        <footer className="mt-12 pt-8 border-t border-gray-700">
          {/* Author Bio */}
          <div className="glass-card p-6 mb-8">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-lg font-semibold">
                  {post.author.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">About {post.author.name}</h3>
                <p className="text-gray-400 mb-4">{post.author.bio}</p>
                <div className="flex space-x-4">
                  <a
                    href="https://www.udemy.com/user/future-x-skill/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-500 hover:text-purple-400 transition-colors"
                  >
                    Udemy
                  </a>
                  <a
                    href="https://linkedin.com/company/futurexskills"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-400 transition-colors"
                  >
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Social Share */}
          <div className="text-center mb-8">
            <p className="text-gray-400 mb-4">Found this article helpful? Share it with others!</p>
            <div className="flex justify-center space-x-4">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Share on Twitter
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900 transition-colors"
              >
                Share on LinkedIn
              </a>
            </div>
          </div>
        </footer>
      </div>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gray-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <BlogCard key={relatedPost.id} post={relatedPost} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="glass-card p-8">
            <h3 className="text-2xl font-bold mb-4">Stay Updated</h3>
            <p className="text-gray-400 mb-6">
              Get more insights like this delivered to your inbox weekly. Join our community of AI professionals.
            </p>
            <a
              href="/#newsletter"
              className="btn-primary"
            >
              Subscribe to Newsletter
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;